import { inject, Injectable, signal } from '@angular/core';
import {
  AssetApiControllerInsightPartnerService,
  MonitoringApiControllerInsightPartnerService,
  ProductResponseDTO,
  UpdateProductRequestDTO,
} from '@generated-apis/index';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { filter, of } from 'rxjs';
import { DashboardService } from '../dashboard/dashboard.service';
import { showSnackbarError } from '@shared/utils/shared-helpers';
import { NETWORK_ERROR_MESSAGE, NETWORK_ERROR_TITLE } from '@shared/models/translations';
import { SnackbarV3Service } from '@gea/digital-ui-lib';

@Injectable({
  providedIn: 'root',
})
export class MachineService {
  private readonly assetApi = inject(AssetApiControllerInsightPartnerService);
  private readonly dashboardService = inject(DashboardService);
  private readonly snackbarService = inject(SnackbarV3Service);

  private readonly _isLoading = signal<boolean>(false);
  private readonly _machine = signal<ProductResponseDTO>({});
  private readonly _machineUUID = signal<string>('');
  private readonly monitoringApiService = inject(MonitoringApiControllerInsightPartnerService);

  readonly isLoading = this._isLoading.asReadonly();
  readonly machine = this._machine.asReadonly();
  readonly machineUUID = this._machineUUID.asReadonly();

  private readonly loadMachineOnUuidChange = toObservable(this._machineUUID)
    .pipe(
      filter((uuid) => !!uuid),
      switchMap((uuid) => {
        return this.loadMachineData(uuid);
      })
    )
    .subscribe();

  setMachineUUID(uuid: string) {
    this._machineUUID.set(uuid);
  }

  resetData() {
    this._machine.set({});
    this._machineUUID.set('');
  }

  loadMachineData(uuid: string) {
    this._isLoading.set(true);
    return this.assetApi.getProduct(uuid).pipe(
      map((dairyData: ProductResponseDTO) => {
        this._machine.set(dairyData);
        return dairyData;
      }),
      catchError(() => {
        showSnackbarError(this.snackbarService, NETWORK_ERROR_TITLE, NETWORK_ERROR_MESSAGE);
        return of(null);
      }),
      finalize(() => {
        this._isLoading.set(false);
      })
    );
  }

  updateMachine(nickName: string) {
    const body: UpdateProductRequestDTO = {
      machineNickname: nickName,
      geaPUID: this._machineUUID(),
    };

    return this.assetApi.updateProduct(body).pipe(
      map((updatedData) => {
        this._machine.update((machine) => {
          return {
            ...machine,
            nickname: updatedData.machineNickname,
          };
        });

        // update nickname in the dashboard service as well without sending another BE request
        this.dashboardService.updateEntry(this._machineUUID(), updatedData.machineNickname);

        return this._machine();
      }),
      catchError(() => {
        return of(null);
      })
    );
  }

  public getLicenses() {
    return this.monitoringApiService.getMonitoringLicenses(this.machineUUID()).pipe(
      map((licenses) => licenses.licenses),
      filter((licences) => licences !== undefined)
    );
  }
}
